import Loader from "components/DesignSystem/Loader/loader";
import { Button } from "components/DesignSystem/Button/Button";
import { Checkbox } from "components/DesignSystem/Checkbox/Checkbox";
import Modal from "components/DesignSystem/Modal/Modal";
import { Form, Formik } from "formik";
import { useCurrentGroupContext } from "hooks/useCurrentGroupContext";
import { useModal } from "hooks/useModal";
import { useRoleBasedView } from "hooks/useRoleBasedView";
import { useToast } from "hooks/useToast";
import { useState } from "react";
import { useParams } from "react-router-dom";
import {
  useEditEntityStateDataMutation,
  useGetEntityAllStateDataQuery,
  useGetEntityStateDataQuery,
  useSetHomeStateMutation,
} from "store/apis/entity";
import { EntityDataAttribute } from "types/Models/entity";
import { BackendError } from "types/utils/error";
import { ModalProps } from "types/utils/modal";
import { ValueType } from "./AddJurisdiction";
import AddNewField from "./AddNewField";
import { ConfirmReplaceHomeStateModal } from "./ConfirmReplaceHomeStateModal";
import { FieldBuilder } from "./FieldBuilder";
import { HOME_STATE } from "./Jurisdictions";

const IS_HOME_STATE_FIELD = "is_home_state";

const EditJurisdiction = ({
  close,
  isOpen,
  stateId,
  entityId: entityIdFromProps,
  modal = false,
}: ModalProps & { stateId: string; entityId?: string; modal?: boolean }) => {
  const { isAdmin } = useRoleBasedView();
  const { successToast, alertToast } = useToast();
  const { entityId: entityIdFromParams } = useParams<{ entityId: string }>();
  const entityId = entityIdFromParams || entityIdFromProps || "";
  const { uuid: groupId } = useCurrentGroupContext();
  const [addEntityState] = useEditEntityStateDataMutation();
  const [setHomeState, { isLoading: isSettingHomeState }] =
    useSetHomeStateMutation();
  const replaceHomeStateWarning = useModal();
  const [pendingValues, setPendingValues] = useState<ValueType | null>(null);

  const { data = [], isLoading } = useGetEntityStateDataQuery(
    {
      groupId,
      entityId: entityId!,
      stateId,
    },
    { skip: !groupId || !entityId || !stateId || !isOpen }
  );

  const { data: allStates = [] } = useGetEntityAllStateDataQuery(
    { groupId, entityId: entityId! },
    { skip: !groupId || !entityId || !isOpen }
  );

  const isCurrentlyHomeState = allStates.some(
    (state) =>
      state.state_id === stateId && state.registration_type === HOME_STATE
  );
  const currentHomeState = allStates.find(
    (state) =>
      state.registration_type === HOME_STATE && state.state_id !== stateId
  );

  const initialValues = data.reduce(
    (acc, field) => {
      acc[field.name] = field.value;
      return acc;
    },
    { stateId, [IS_HOME_STATE_FIELD]: isCurrentlyHomeState } as ValueType
  );

  const saveJurisdiction = async (values: ValueType) => {
    const { stateId, [IS_HOME_STATE_FIELD]: markAsHomeState, ...rest } = values;
    try {
      await addEntityState({
        groupId,
        entityId,
        stateId: stateId as string,
        payload: rest,
      }).unwrap();
      if (markAsHomeState && !isCurrentlyHomeState) {
        await setHomeState({
          groupId,
          entityId,
          stateId: stateId as string,
        }).unwrap();
      }
      successToast({ message: "Entity updated successfully" });
      close();
    } catch (error) {
      alertToast({ message: (error as BackendError).data?.error?.message });
    }
  };

  const onSubmit = async (values: ValueType) => {
    const markAsHomeState = values[IS_HOME_STATE_FIELD];
    if (markAsHomeState && !isCurrentlyHomeState && currentHomeState) {
      setPendingValues(values);
      replaceHomeStateWarning.open();
      return;
    }
    await saveJurisdiction(values);
  };

  const handleConfirmReplaceHomeState = async () => {
    if (pendingValues) {
      await saveJurisdiction(pendingValues);
    }
    setPendingValues(null);
    replaceHomeStateWarning.close();
  };

  return (
    <Modal.Root onOpenChange={close} open={isOpen} modal={modal}>
      <Formik
        initialValues={initialValues}
        onSubmit={onSubmit}
        enableReinitialize
      >
        {({ submitForm, isSubmitting, values, setFieldValue }) => (
          <Modal.Content useCustomOverlay>
            <Modal.Header>
              <Modal.Title>Edit State</Modal.Title>
              <Modal.Close />
            </Modal.Header>
            <Modal.Body>
              {isLoading ? (
                <Loader />
              ) : (
                <Form className="t-flex t-flex-col t-gap-5 t-m-0">
                  <>
                    {data.map((field) => (
                      <FieldBuilder field={field} key={field.uuid} />
                    ))}
                    {isAdmin && (
                      <Checkbox
                        name={IS_HOME_STATE_FIELD}
                        label="Mark this state as home state"
                        checked={Boolean(values?.[IS_HOME_STATE_FIELD])}
                        disabled={isCurrentlyHomeState}
                        onChange={(e) =>
                          setFieldValue(IS_HOME_STATE_FIELD, e.target.checked)
                        }
                      />
                    )}
                    {isAdmin && values?.stateId && (
                      <AddNewField
                        stateId={values?.stateId as string}
                        entityId={entityId}
                      />
                    )}
                  </>
                </Form>
              )}
            </Modal.Body>
            <Modal.FooterButtonGroup>
              <Modal.RawClose asChild>
                <Button onClick={close} disabled={isSubmitting}>
                  Cancel
                </Button>
              </Modal.RawClose>
              <Button
                customType="primary"
                disabled={isSubmitting}
                isLoading={isSubmitting}
                onClick={submitForm}
              >
                Save
              </Button>
            </Modal.FooterButtonGroup>
            <ConfirmReplaceHomeStateModal
              open={replaceHomeStateWarning.isOpen}
              onClose={replaceHomeStateWarning.close}
              onConfirm={handleConfirmReplaceHomeState}
              isLoading={isSubmitting || isSettingHomeState}
              currentHomeStateName={currentHomeState?.state || ""}
              newHomeStateName={
                allStates.find((state) => state.state_id === stateId)?.state ||
                ""
              }
            />
          </Modal.Content>
        )}
      </Formik>
    </Modal.Root>
  );
};

export default EditJurisdiction;
